import React, { useRef, useContext, useEffect } from 'react';
import { Observer } from 'mobx-react-lite';
import List from 'devextreme-react/list';
import DataSource from 'devextreme/data/data_source';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { toast } from 'react-toastify';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import { renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';

import BookingUserUpdateStore from '../../../../stores/BookingUserUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';

import './ManageBookingUsers.scss';

function ManageBookingUsers(props) {
    const isMounted = useRef(true);
    const confirmModalRef = useRef(null);
    const focusTimer = useRef(null);
    const bookingUserUpdate = useContext(BookingUserUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        return (() => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (bookingUserUpdate.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (bookingUserUpdate.removeUsers && bookingUserUpdate.removeUsers.length > 0) {
            confirmModalRef.current.show({
                icon: <i className='fal fa-times-square text-danger mr-2'></i>,
                message: <>Continue to <span className='fw-500'>&nbsp;disable online booking for&nbsp;{bookingUserUpdate.removeUsers.length === 1 ? bookingUserUpdate.removeUsers[0].fullName : `${bookingUserUpdate.removeUsers.length} users`}</span>? </>,
                onOption1Click: handleSubmitConfirm,
                onCancel: handleSubmitCancel,
            })
        }
        else {
            handleSubmitConfirm();
        }
    }

    const handleSubmitConfirm = () => {
        bookingUserUpdate.update()
            .then(() => {
                confirmModalRef.current.close();
                toast.dark(() => <p>Booking Users Updated.</p>);

                if (fn.isFunction(props.onSuccess)) {
                    props.onSuccess();
                }
            })
    }

    const handleSubmitCancel = () => {
        confirmModalRef.current.close();
    }

    const handleAcceptOnlineBookingChange = (event, id) => {
        const index = bookingUserUpdate.data.findIndex(d => d.id === id);

        if (index >= 0) {
            bookingUserUpdate.data[index].acceptOnlineBooking = !bookingUserUpdate.data[index].acceptOnlineBooking;
        }
    }

    const renderBookingUser = (item) => {
        return <Observer>{() =>
            <div className='form-group mb-0 py-1'>
                <div className='booking-user d-flex w-100 align-items-center'>
                    <label
                        htmlFor={`booking-user-${item.id}`}
                        className={'booking-user-name flex-1 d-block text-truncate text-info-hover' + (!item.acceptOnlineBooking ? ' text-gray-600' : '')}
                    >
                        <strong>{item.fullName}</strong>
                    </label>
                    <div className='text-right'>
                        <div className='custom-control custom-switch'>
                            <input
                                id={`booking-user-${item.id}`}
                                type='checkbox'
                                name={`booking-user-${item.id}`}
                                className='custom-control-input'
                                checked={!!item.acceptOnlineBooking}
                                onChange={(e) => { handleAcceptOnlineBookingChange(e, item.id) }}
                            />
                            <label
                                htmlFor={`booking-user-${item.id}`}
                                className='custom-control-label'
                            >
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        }</Observer>
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={bookingUserUpdate.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={'fal fa-users-medical'}
                        action='Manage'
                        category='Online Booking Users'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <div className='manage-booking-users-page body-content'>
                            <Observer>{() =>
                                <>
                                    {
                                        bookingUserUpdate.isReady ?
                                            <FadeIn>
                                                <section>
                                                    <div className='row datasets'>
                                                        <div className='col-12'>
                                                            <div className='d-flex w-100 align-items-center'>
                                                                <strong className='text-gray-600 text-uppercase flex-1 m-0 mr-2'>Available Users</strong>
                                                            </div>
                                                            <div>
                                                                <List
                                                                    dataSource={
                                                                        new DataSource({
                                                                            key: "id",
                                                                            store: bookingUserUpdate.data,
                                                                            paginate: false,
                                                                        })
                                                                    }
                                                                    activeStateEnabled={false}
                                                                    focusStateEnabled={false}
                                                                    hoverStateEnabled={false}
                                                                    itemRender={(data) => { return renderBookingUser(data) }}
                                                                >
                                                                </List>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </FadeIn> : renderQuickDrawerLoading()
                                    }
                                </>
                            }</Observer>
                        </div>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                    >Update</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default ManageBookingUsers;